import React, { useMemo } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { PieChart, Pie, Cell } from "recharts";
import { LiquidityPeriodBalanceType, Product } from "../../interfaces/DTO/mislaka_xml_models";
import { MimshakGroupByService } from "../../services/MimshakGroupByService";
import { MimshakService } from "../../services/mimshakService";
import { colorHandlerCodeUniversal } from "../../utils/colorEnumFormatter";
import { formatCurrency } from "../../utils/namesFormater";
import { formatHandlerCodeUniversal } from "../../utils/handlerCodeUniversalFormatter";

interface Props {
  products: Product[];
  balanceType?: LiquidityPeriodBalanceType;
  selectedHandler?: string | null;
  onHandlerFilter: (handlerCode: string | null) => void;
}

const metricRowSx = {
  color: "#6d1727",
  fontWeight: 500,
};

const GroupByHandlerPieChartWithListInteractive: React.FC<Props> = ({
  products,
  balanceType,
  selectedHandler,
  onHandlerFilter,
}) => {
  const groupedData = useMemo(
    () => MimshakGroupByService.groupByHandlerCode(products, balanceType),
    [products, balanceType]
  );

  const total = useMemo(
    () =>
      balanceType
        ? MimshakService.calculateTotalFundSum(products, balanceType)
        : MimshakService.calculateTotalsFromProducts(products),
    [products, balanceType]
  );

  const entries = useMemo(() => {
    if (!groupedData) return [];
    return Object.entries(groupedData)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => b - a);
  }, [groupedData]);

  if (entries.length === 0) return null;

  const pieData = entries.map(([code, value]) => ({ name: code, value }));

  const handleRowClick = (code: string) => {
    onHandlerFilter(selectedHandler === code ? null : code);
  };

  return (
    <Box sx={{ textAlign: "center" }}>
      <PieChart width={120} height={120} style={{ margin: "0 auto" }}>
        <Pie
          data={pieData}
          dataKey="value"
          cx={55}
          cy={55}
          outerRadius={55}
          startAngle={90}
          endAngle={-270}
        >
          {pieData.map((entry) => (
            <Cell
              key={entry.name}
              fill={colorHandlerCodeUniversal(entry.name)}
              stroke="#fff"
              strokeWidth={selectedHandler && selectedHandler !== entry.name ? 0 : 4}
              opacity={selectedHandler && selectedHandler !== entry.name ? 0.35 : 1}
            />
          ))}
        </Pie>
      </PieChart>

      <Box sx={{ mt: 1, display: "table", width: "85%", mx: "auto" }}>
        {entries.map(([code, value]) => {
          const pct = total > 0 ? Math.round((value / total) * 100) : 0;
          const color = colorHandlerCodeUniversal(code);
          const isSelected = selectedHandler === code;
          const isDimmed = selectedHandler !== null && selectedHandler !== undefined && !isSelected;

          return (
            <Box
              key={code}
              onClick={() => handleRowClick(code)}
              sx={{
                display: "table-row",
                cursor: "pointer",
                opacity: isDimmed ? 0.4 : 1,
                bgcolor: isSelected ? `${color}18` : "transparent",
                borderRadius: 1,
                transition: "opacity 0.15s",
                "&:hover": { opacity: 1 },
              }}
            >
              <Box sx={{ display: "table-cell", verticalAlign: "middle", textAlign: "right" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end", flexDirection: "row-reverse" }}>
                  <Typography variant="body1" sx={{ ...metricRowSx, textAlign: "right", fontWeight: isSelected ? 700 : 500 }}>
                    {formatHandlerCodeUniversal(code)}
                  </Typography>
                  <Chip
                    label={`${pct}%`}
                    size="small"
                    sx={{ bgcolor: color, color: "#fff", fontWeight: 700, borderRadius: "6px", mb: "5px", flexShrink: 0 }}
                  />
                </Box>
              </Box>
              <Typography
                variant="body1"
                sx={{ ...metricRowSx, display: "table-cell", textAlign: "right", width: "100px", fontWeight: isSelected ? 700 : 500 }}
              >
                {formatCurrency(value)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {selectedHandler && (
        <Box
          onClick={() => onHandlerFilter(null)}
          sx={{ mt: 1, cursor: "pointer", fontSize: 12, color: "text.secondary", textDecoration: "underline" }}
        >
          נקה פילטר
        </Box>
      )}
    </Box>
  );
};

export default GroupByHandlerPieChartWithListInteractive;
